import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { setLanguage } from '@repo/locale-client';
import { loadLanguageResources } from './utils/loadI18nResources';
export const i18n = i18next;
const DEFAULT_LANGUAGE = 'pl';
const PACKAGE_LANGUAGES = ['en', 'pl'];
/**
 * Packages own their strings and resolve them through `@repo/locale-client`, so
 * the only thing they need from an application is the active language.
 *
 * Doing it here rather than in each application means the two cannot drift apart
 * and no application has to remember the call. i18next's language is an
 * arbitrary string, so it is narrowed to a language packages actually ship.
 */
function syncPackageLanguage(language) {
    setLanguage(PACKAGE_LANGUAGES.find((candidate) => candidate === language) ?? DEFAULT_LANGUAGE);
}
async function addLanguageBundles(language) {
    const resources = await loadLanguageResources(language);
    for (const namespace in resources) {
        // deep + overwrite so app resources win over any shared defaults already present
        i18n.addResourceBundle(language, namespace, resources[namespace], true, true);
    }
}
/**
 * Initializes i18next with only the active language's resources, so the inactive
 * language is never shipped at boot. Other languages are fetched on demand by
 * {@link changeLanguage}. Returns once the active language is ready — await it
 * before rendering so synchronous `tr()` calls resolve.
 */
export async function initI18n(initialLanguage = DEFAULT_LANGUAGE) {
    const resources = await loadLanguageResources(initialLanguage);
    await i18n.use(initReactI18next).init({
        resources: { [initialLanguage]: resources },
        lng: initialLanguage,
        defaultNS: 'common',
        fallbackNS: 'common',
        fallbackLng: DEFAULT_LANGUAGE,
        interpolation: {
            escapeValue: false,
        },
        react: {
            useSuspense: false,
        },
    });
    syncPackageLanguage(initialLanguage);
    // Same handler reference each time, so re-initializing replaces the listener
    // rather than stacking another one.
    i18n.off('languageChanged', syncPackageLanguage);
    i18n.on('languageChanged', syncPackageLanguage);
    // The fallback language backs missing keys. Fetch it in the background when it
    // is not the active one so a missing key never renders as a raw key.
    if (initialLanguage !== DEFAULT_LANGUAGE) {
        void addLanguageBundles(DEFAULT_LANGUAGE);
    }
}
/**
 * Switches the active language, fetching its resources first if they have not
 * been loaded yet. Use this instead of `i18n.changeLanguage` directly so the
 * switch never renders untranslated keys.
 */
export async function changeLanguage(language) {
    if (!i18n.hasResourceBundle(language, 'common')) {
        await addLanguageBundles(language);
    }
    await i18n.changeLanguage(language);
}
